import { useState, useMemo } from "react";
import { X, Heart } from "lucide-react";
import { fromISO, formatDayShort } from "../../utils/date.js";

function nightsBetween(startISO, endISO) {
  const ms = fromISO(endISO).getTime() - fromISO(startISO).getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
}

function buildMumText({ tripTitle, legs, items }) {
  const sortedLegs = [...legs].sort((a, b) => a.start.localeCompare(b.start));
  const lines = [];

  lines.push(tripTitle);
  if (sortedLegs.length > 0) {
    const first = sortedLegs[0].start;
    const last = sortedLegs[sortedLegs.length - 1].end;
    lines.push(`${formatDayShort(fromISO(first))} – ${formatDayShort(fromISO(last))}`);
  }
  lines.push("");

  for (const leg of sortedLegs) {
    const nights = nightsBetween(leg.start, leg.end);
    const header = `${leg.emoji ? leg.emoji + " " : ""}${leg.city}, ${leg.country}${leg.tentative ? " (not yet confirmed)" : ""}`;
    lines.push(header);
    lines.push(`${formatDayShort(fromISO(leg.start))} – ${formatDayShort(fromISO(leg.end))} · ${nights} night${nights === 1 ? "" : "s"}`);

    const accomItems = Object.values(items)
      .filter((it) => it.legId === leg.id)
      .filter((it) => it.category === "accom")
      .filter((it) => it.status === "booked" || it.status === "done");

    if (accomItems.length === 0) {
      lines.push("  (no accommodation booked yet)");
    } else {
      const sorted = accomItems.sort((a, b) => a.date.localeCompare(b.date));
      for (const it of sorted) {
        const end = it.endDate && it.endDate !== it.date
          ? ` – ${formatDayShort(fromISO(it.endDate))}`
          : "";
        lines.push(`  • ${it.title} (${formatDayShort(fromISO(it.date))}${end})`);
      }
    }
    lines.push("");
  }

  return lines.join("\n").trimEnd();
}

export default function MumModeModal({ open, onClose, tripTitle, legs, items }) {
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => {
    if (!open) return "";
    return buildMumText({ tripTitle, legs, items });
  }, [open, tripTitle, legs, items]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.getElementById("mum-mode-textarea");
      if (ta) {
        ta.select();
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Heart size={18} className="text-rose-500" />
            <h2
              className="text-xl font-semibold text-slate-900 dark:text-slate-100"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              Mum mode
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
            A high-level summary, safe to share with family. Shows where you'll be staying each leg — no activities, notes, links, or addresses.
          </p>
          <textarea
            id="mum-mode-textarea"
            readOnly
            value={text}
            onClick={(e) => e.target.select()}
            className="w-full h-64 p-3 text-xs font-mono bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl resize-none text-slate-700 dark:text-slate-300 whitespace-pre"
          />
          <button
            onClick={copy}
            className="w-full px-4 py-2.5 rounded-full bg-slate-900 dark:bg-slate-600 text-white font-medium hover:bg-slate-700 dark:hover:bg-slate-500 transition-colors text-sm"
          >
            {copied ? "Copied ✓" : "Copy to clipboard"}
          </button>
        </div>
      </div>
    </div>
  );
}
